/**
 * F-31 "open this card's link" — the ONE definition of WHAT a link card opens
 * and WHERE, shared by the double-click on the card itself (ElementsLayer) and
 * the F-27 toolbar's open button (./toolbar.tsx), so those two entry points
 * can never drift apart on which URLs are allowed or where they land.
 *
 * A different question from `features/elements/link-url.ts`'s `singleHttpUrl`,
 * which is why this is not a call into it: that one asks "is this PASTED TEXT
 * one URL rather than tabular text?" (hence its no-interior-whitespace rule).
 * This one asks "is this STORED url safe to hand to the browser?" — a scheme
 * gate, because `payload.url` is doc content that any client (or a future
 * import path) could have written, and a `javascript:` URL must never reach
 * `window.open`.
 */
import type { ElementInfo } from "@kola/shared";
import { openExternalUrl } from "../../../core/host.js";

/**
 * The http(s) URL this element opens, or null when it has none — not a link
 * card at all, or a `url` that is not a parseable http/https address. Returned
 * in canonical `URL.href` form so the string that gets opened is exactly the
 * one that was validated, never a raw variant that re-parses differently.
 *
 * Deliberately blind to `payload.status`: a card is openable while it is still
 * unfurling and after the unfurl FAILED (F-31's domain-only fallback is
 * explicitly still a link) — the URL is the card's identity, the metadata is
 * only its face.
 */
export function linkCardUrl(element: ElementInfo): string | null {
  const payload = element.payload;
  if (payload.kind !== "linkCard") return null;
  let url: URL;
  try {
    url = new URL(payload.url);
  } catch {
    return null; // not a parseable absolute URL
  }
  if (url.protocol !== "http:" && url.protocol !== "https:") return null;
  return url.href;
}

/**
 * Hand a URL to the user's browser — through the shell (core/host.ts), which
 * is the one place that knows how a link leaves Kola. In the web shell that
 * is `window.open(_blank)`, and deliberately nothing else, because every
 * browser host answers exactly that with the OS DEFAULT BROWSER rather than
 * a chrome-less view (docs/02 F-31): the Flutter shell turns the resulting
 * off-origin navigation into `launchUrl(externalApplication)`
 * (kola_screen.dart `onNavigationRequest`), and a plain browser opens a tab.
 * Inside the desktop app every `window.open` is denied (its windows never
 * spawn a chrome-less child), so the embedded host hands the URL to
 * `shell.openExternal` itself. Reaching for anything else here would land in
 * precisely the chrome-less window F-31 exists to avoid.
 *
 * `noopener,noreferrer` (web shell): the opened page gets no `window.opener`
 * handle back into the board.
 */
export function openLinkInBrowser(url: string): void {
  openExternalUrl(url);
}
